/**
 * API Client for Tags Dictionary
 *
 * Tags are user-editable dictionary entries used in notes (table/column).
 */

import { httpClient } from './client';
import {
  assertObject,
  assertEntityId,
  assertString,
  assertBoolean,
  assertNumber,
  assertOptionalString,
  assertOptionalEntityId,
  assertArrayOf,
} from '../types/validation';

export function validateTag(data: unknown, path: string): ITag {
  const obj = assertObject(data, path);
  return {
    id: assertEntityId(obj['id'], `${path}.id`),
    code: assertString(obj['code'], `${path}.code`),
    name: assertString(obj['name'], `${path}.name`),
    description: assertOptionalString(obj['description'], `${path}.description`),
    color: assertOptionalString(obj['color'], `${path}.color`),
    icon: assertOptionalString(obj['icon'], `${path}.icon`),
    categoryId: assertOptionalEntityId(obj['categoryId'], `${path}.categoryId`),
    isSystem: assertBoolean(obj['isSystem'], `${path}.isSystem`),
    displayOrder: assertNumber(obj['displayOrder'], `${path}.displayOrder`),
  };
}

// ============================================================================
// Type Definitions
// ============================================================================

export interface ITag {
  readonly id: string; // UUID
  readonly code: string;
  readonly name: string;
  readonly description?: string;
  readonly color?: string;
  readonly icon?: string;
  readonly categoryId?: string;
  readonly isSystem: boolean;
  readonly displayOrder: number;
}

export interface ICreateTagRequest {
  readonly code: string;
  readonly name: string;
  readonly description?: string;
  readonly color?: string;
  readonly categoryId?: string;
}

export interface ITagsApiClient {
  listTags(projectId: string): Promise<readonly ITag[]>;
  createTag(projectId: string, request: ICreateTagRequest): Promise<ITag>;
  deleteTag(projectId: string, tagId: string): Promise<void>;
}

class TagsApiClient implements ITagsApiClient {
  async listTags(projectId: string): Promise<readonly ITag[]> {
    const response = await httpClient.get<unknown>(`/projects/${projectId}/tags`);
    return assertArrayOf(response.data, validateTag, 'listTags');
  }

  async createTag(projectId: string, request: ICreateTagRequest): Promise<ITag> {
    try {
      const response = await httpClient.post<unknown>(`/projects/${projectId}/tags`, request);
      return validateTag(response.data, 'createTag');
    } catch (error: unknown) {
      if (error !== null && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown; statusText?: string } };
        const errorText = axiosError.response?.statusText ?? 'Unknown error';
        throw new Error(`Failed to create tag: ${errorText}`);
      }
      throw error;
    }
  }

  async deleteTag(projectId: string, tagId: string): Promise<void> {
    try {
      await httpClient.delete(`/projects/${projectId}/tags/${encodeURIComponent(tagId)}`);
    } catch (error: unknown) {
      if (error !== null && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown; statusText?: string } };
        const errorText = axiosError.response?.statusText ?? 'Unknown error';
        throw new Error(`Failed to delete tag: ${errorText}`);
      }
      throw error;
    }
  }
}

let tagsApiClient: ITagsApiClient | null = null;

export function getTagsApiClient(): ITagsApiClient {
  tagsApiClient ??= new TagsApiClient();
  return tagsApiClient;
}

